/**
 * 媒体信息处理模块
 * 支持格式：MPEG-4、JPEG、JPG、PNG
 */

// 常量定义
const CONSTANTS = {
    SUPPORTED_FORMATS: ['MPEG-4', 'JPEG', 'JPG', 'PNG'],
    DEFAULT_CHUNK_SIZE: 256 * 1024, // 256KB
    MEDIA_INFO_OPTIONS: {
        format: 'object'
    }
};

// 全局变量
let mediaInfoInstance = null;
const fileQueue = [];
let isProcessing = false;

/**
 * 初始化 MediaInfo 实例
 * @returns {Promise<Object>} MediaInfo 实例
 */
const initializeMediaInfo = () => {
    return new Promise((resolve) => {
        MediaInfo(CONSTANTS.MEDIA_INFO_OPTIONS, (mediaInfo) => {
            resolve(mediaInfo);
        });
    });
};

/**
 * 读取文件块
 * @param {File} file - 文件对象
 * @param {number} chunkSize - 块大小
 * @param {number} offset - 偏移量
 * @returns {Promise<Uint8Array>} 文件块数据
 */
const readFileChunk = (file, chunkSize, offset) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target.error) {
                reject(event.target.error);
                return;
            }
            resolve(new Uint8Array(event.target.result));
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
    });
};

/**
 * 获取文件大小
 * @param {File} file - 文件对象
 * @returns {number} 文件大小
 */
const getFileSize = (file) => file.size;

/**
 * 处理媒体信息结果
 * @param {Object} result - MediaInfo 分析结果
 * @returns {Object} 处理后的媒体信息
 */
const processMediaInfoResult = (result) => {
    const track = result.media.track[0];
    const track1 = result.media.track[1];
    
    const mediaInfo = {
        name: result.creatingLibrary.name,
        format: track.Format || '',
        fileSize: Number(track.FileSize)
    };

    if (track1) {
        mediaInfo.width = Number(track1.Width);
        mediaInfo.height = Number(track1.Height);
        if (track1.Duration) {
            mediaInfo.duration = Number(track1.Duration);
        }
    }

    return mediaInfo;
};

/**
 * 分析单个文件的媒体信息
 * @param {File} file - 文件对象
 * @returns {Promise<Object>} 媒体信息
 */
const analyzeMediaInfo = async (file) => {
    if (!file) {
        throw new Error('文件不能为空');
    }

    if (!mediaInfoInstance) {
        mediaInfoInstance = await initializeMediaInfo();
    }

    try {
        const result = await mediaInfoInstance.analyzeData(
            () => getFileSize(file),
            (chunkSize, offset) => readFileChunk(file, chunkSize, offset)
        );

        return processMediaInfoResult(result);
    } catch (error) {
        console.error('媒体信息分析失败:', error);
        throw error;
    }
};

/**
 * 处理文件队列
 */
const processFileQueue = async () => {
    if (isProcessing || fileQueue.length === 0) {
        return;
    }

    isProcessing = true;

    try {
        while (fileQueue.length > 0) {
            const { file, resolve, reject } = fileQueue.shift();
            try {
                const result = await analyzeMediaInfo(file);
                resolve(result);
            } catch (error) {
                reject(error);
            }
        }
    } finally {
        isProcessing = false;
    }
};

/**
 * 获取媒体文件信息
 * @param {File} file - 文件对象
 * @returns {Promise<Object>} 媒体信息
 */
const getMediaInfo = (file) => {
    return new Promise((resolve, reject) => {
        fileQueue.push({ file, resolve, reject });
        processFileQueue();
    });
};

export default getMediaInfo;


